import Analytics from '@withlocals/withlocals-analytics';
import type {
  NotificationClickEvent,
  NotificationWillDisplayEvent,
  OSNotification,
} from 'onesignal-cordova-plugin';
import { PushMessage } from 'types/PushNotification';
import { WlWindow } from 'typings/globals/cordova';
import Logger from 'utils/Logger';
import { isCordova } from 'utils/platform';
import { permissions } from '~/cordovaPlugins/Permissions';
import { useDeviceInfoStore } from '~/stores/device';

declare const window: WlWindow;

type NotificationCallback = (data: PushMessage) => void;
type Unsubscribe = () => void;

// OneSignal log levels: 0=NONE, 1=FATAL, 2=ERROR, 3=WARN, 4=INFO, 5=DEBUG, 6=VERBOSE
const ONESIGNAL_LOG_LEVEL = 4;

function isOneSignalAvailable(): boolean {
  return isCordova() && !!window?.plugins?.OneSignal;
}

function getOneSignalPlugin() {
  if (!isOneSignalAvailable()) return null;
  return window.plugins?.OneSignal ?? null;
}

export interface OneSignalServiceType {
  onNotificationOpened: (callback: NotificationCallback) => Unsubscribe;
  onNotificationReceived: (callback: NotificationCallback) => Unsubscribe;
  requestPushPermissions: (fallbackToSettings?: boolean) => Promise<boolean>;
  hasPermission: () => Promise<boolean>;
  destroy: () => void;
}

export function getAnonymousId(): Promise<string> {
  return useDeviceInfoStore.getState().getOrCreateAnonymousDeviceId();
}

function loginAsAnonymous(anonymousId: string): void {
  const oneSignal = getOneSignalPlugin();
  if (!oneSignal) return;

  oneSignal.login(anonymousId);
  oneSignal.User.addTags({
    anonymousDeviceId: anonymousId,
    isAuthenticated: 'false',
  });
}

export async function initializeForAnonymous(): Promise<void> {
  if (!getOneSignalPlugin()) {
    Logger.log(
      '[onesignal] Cannot initialize for anonymous - plugin not available',
    );
    return;
  }

  const anonymousId = await getAnonymousId();
  Logger.log('[onesignal] Initializing for anonymous user:', anonymousId);
  loginAsAnonymous(anonymousId);
}

export async function migrateToAuthenticatedUser(
  accountId: string,
): Promise<void> {
  if (!accountId?.trim()) {
    Logger.log('[onesignal] Cannot migrate - invalid accountId');
    return;
  }

  const oneSignal = getOneSignalPlugin();
  if (!oneSignal) {
    Logger.log(
      '[onesignal] Cannot migrate to authenticated - plugin not available',
    );
    return;
  }

  const anonymousId = await getAnonymousId();
  Logger.log(
    '[onesignal] Migrating from anonymous to authenticated:',
    accountId,
  );

  oneSignal.logout();
  oneSignal.login(accountId);
  oneSignal.User.addTags({
    accountId: accountId,
    isAuthenticated: 'true',
    previousAnonymousDeviceId: anonymousId,
  });
  oneSignal.User.removeTag('anonymousDeviceId');
}

export async function revertToAnonymous(): Promise<void> {
  const oneSignal = getOneSignalPlugin();
  if (!oneSignal) {
    Logger.log('[onesignal] Cannot revert to anonymous - plugin not available');
    return;
  }

  const anonymousId = await getAnonymousId();
  Logger.log('[onesignal] Reverting to anonymous user:', anonymousId);

  oneSignal.logout();
  loginAsAnonymous(anonymousId);
  oneSignal.User.removeTags(['accountId', 'previousAnonymousDeviceId']);
}

function extractPushMessage(notification: OSNotification): PushMessage | null {
  if (!notification?.additionalData) {
    return null;
  }
  return notification.additionalData as PushMessage;
}

function createNoOpService(): OneSignalServiceType {
  return {
    onNotificationOpened: () => () => {},
    onNotificationReceived: () => () => {},
    requestPushPermissions: async () => false,
    hasPermission: async () => false,
    destroy: () => {},
  };
}

export function OneSignalService(): OneSignalServiceType {
  Logger.log('[onesignal] Starting service with id:', ONESIGNAL_ID);

  const oneSignal = getOneSignalPlugin();
  if (!oneSignal) {
    Logger.log(
      '[onesignal] Service not started - plugin not available (non-mobile environment)',
    );
    return createNoOpService();
  }

  oneSignal.initialize(ONESIGNAL_ID);

  if (ENVIRONMENT !== 'production') {
    oneSignal.Debug.setLogLevel(ONESIGNAL_LOG_LEVEL);
  }

  void initializeForAnonymous();

  // Listen for permission changes (fires when user allows/denies via native prompt)
  const handlePermissionChange = (granted: boolean) => {
    Logger.log('[onesignal] Permission changed:', granted);
    permissions();
  };
  oneSignal.Notifications.addEventListener(
    'permissionChange',
    handlePermissionChange,
  );

  const openedCallbacks = new Set<NotificationCallback>();
  const receivedCallbacks = new Set<NotificationCallback>();

  const handleClick = (event: NotificationClickEvent): void => {
    const pushMessage = extractPushMessage(event.notification);
    if (pushMessage) {
      openedCallbacks.forEach((cb) => {
        try {
          cb(pushMessage);
        } catch (error) {
          Logger.error(
            '[onesignal] Error in notification opened callback:',
            error,
          );
        }
      });
    }
  };

  const handleForegroundDisplay = (
    event: NotificationWillDisplayEvent,
  ): void => {
    const notification = event.getNotification();
    const pushMessage = extractPushMessage(notification);

    if (pushMessage) {
      receivedCallbacks.forEach((cb) => {
        try {
          cb(pushMessage);
        } catch (error) {
          Logger.error(
            '[onesignal] Error in notification received callback:',
            error,
          );
        }
      });
    }

    notification.display();
  };

  oneSignal.Notifications.addEventListener('click', handleClick);
  oneSignal.Notifications.addEventListener(
    'foregroundWillDisplay',
    handleForegroundDisplay,
  );

  const onNotificationOpened = (
    callback: NotificationCallback,
  ): Unsubscribe => {
    openedCallbacks.add(callback);
    return () => {
      openedCallbacks.delete(callback);
    };
  };

  const onNotificationReceived = (
    callback: NotificationCallback,
  ): Unsubscribe => {
    receivedCallbacks.add(callback);
    return () => {
      receivedCallbacks.delete(callback);
    };
  };

  const requestPushPermissions = async (
    fallbackToSettings = false,
  ): Promise<boolean> => {
    const hasPermissionAlready =
      await oneSignal.Notifications.getPermissionAsync();

    if (hasPermissionAlready) {
      return true;
    }

    const accepted = await oneSignal.Notifications.requestPermission(true);

    Analytics.track('Push', 'Permission requested', { accepted });

    if (!accepted && fallbackToSettings) {
      Analytics.track('Push', 'Redirecting to settings');
      window?.cordova?.plugins?.diagnostic?.switchToSettings?.();
    }

    return accepted;
  };

  const hasPermission = async (): Promise<boolean> => {
    return oneSignal.Notifications.getPermissionAsync();
  };

  const destroy = (): void => {
    oneSignal.Notifications.removeEventListener('click', handleClick);
    oneSignal.Notifications.removeEventListener(
      'permissionChange',
      handlePermissionChange,
    );
    oneSignal.Notifications.removeEventListener(
      'foregroundWillDisplay',
      handleForegroundDisplay,
    );
    openedCallbacks.clear();
    receivedCallbacks.clear();
  };

  return {
    onNotificationOpened,
    onNotificationReceived,
    requestPushPermissions,
    hasPermission,
    destroy,
  };
}
